import { useState } from 'react';
import { Button, Col, Form, Input, InputNumber, Modal, Row, Select, Steps, Typography } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';

const { Text } = Typography;
const { TextArea } = Input;

const DEFAULT_STEPS = [
  {
    key: 'basic',
    title: 'Basic Info',
    description: 'General details',
    fields: [
      { name: 'firstName', label: 'First Name', type: 'input', required: true, placeholder: 'Enter first name', span: 12 },
      { name: 'lastName', label: 'Last Name', type: 'input', required: true, placeholder: 'Enter last name', span: 12 },
      { name: 'email', label: 'Email Address', type: 'input', required: true, placeholder: 'Enter email address', rules: [{ type: 'email', message: 'Please enter a valid email' }] },
      { name: 'phone', label: 'Phone', type: 'input', placeholder: 'Enter phone number' },
    ],
  },
  {
    key: 'details',
    title: 'Details',
    description: 'Additional info',
    fields: [
      {
        name: 'role', label: 'Role', type: 'select', required: true, placeholder: 'Select a role', span: 12,
        options: [
          { label: 'Developer', value: 'developer' },
          { label: 'Designer', value: 'designer' },
          { label: 'Manager', value: 'manager' },
          { label: 'Analyst', value: 'analyst' },
        ],
      },
      {
        name: 'department', label: 'Department', type: 'select', placeholder: 'Select department', span: 12,
        options: [
          { label: 'Engineering', value: 'engineering' },
          { label: 'Product', value: 'product' },
          { label: 'Operations', value: 'operations' },
          { label: 'Finance', value: 'finance' },
        ],
      },
      { name: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Any additional notes...' },
    ],
  },
  {
    key: 'confirm',
    title: 'Confirm',
    description: 'Review & submit',
    isReview: true,
  },
];

function renderFieldControl(field) {
  switch (field.type) {
    case 'select':
      return <Select placeholder={field.placeholder} options={field.options} style={{ width: '100%' }} />;
    case 'textarea':
      return <TextArea rows={3} placeholder={field.placeholder} />;
    case 'number':
      return <InputNumber placeholder={field.placeholder} style={{ width: '100%' }} />;
    default:
      return <Input type={field.inputType || 'text'} placeholder={field.placeholder} />;
  }
}

function ReviewStep({ steps, form }) {
  const values = form.getFieldsValue();

  const dataSteps = steps.filter((s) => !s.isReview);

  const hasAnyValue = dataSteps.some((step) =>
    (step.fields || []).some((field) => values[field.name] !== undefined && values[field.name] !== ''),
  );

  if (!hasAnyValue) {
    return (
      <div className="wizard-review-empty">
        <Text type="secondary">No data entered yet.</Text>
      </div>
    );
  }

  return (
    <div className="wizard-review-content">
      {dataSteps.map((step) => (
        <div key={step.key} className="wizard-review-section">
          <Text className="wizard-review-section-title">{step.title}</Text>
          <div className="wizard-review-grid">
            {(step.fields || []).map((field) => {
              const raw = values[field.name];
              const displayVal = raw === undefined || raw === '' || raw === null ? '—' : String(raw);
              return (
                <div key={field.name} className="wizard-review-row">
                  <span className="wizard-review-label">{field.label}</span>
                  <span className="wizard-review-separator">:</span>
                  <span className="wizard-review-value">{displayVal}</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <div className="wizard-review-confirm-note">
        <CheckCircleOutlined className="wizard-review-confirm-icon" />
        <Text type="secondary">Please review the information above before submitting.</Text>
      </div>
    </div>
  );
}

export default function WizardPopup() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    title = 'Wizard',
    moduleName = '',
    steps = DEFAULT_STEPS,
    onSubmit,
  } = location.state ?? {};

  const [current, setCurrent] = useState(0);

  const currentStep = steps[current];
  const isLastStep = current === steps.length - 1;
  const isFirstStep = current === 0;

  function handleClose() {
    navigate(-1);
  }

  function handleNext() {
    const stepFieldNames = (currentStep.fields || []).map((f) => f.name);
    form.validateFields(stepFieldNames.length > 0 ? stepFieldNames : undefined).then(() => {
      setCurrent((prev) => prev + 1);
    });
  }

  function handleBack() {
    setCurrent((prev) => prev - 1);
  }

  function handleSubmit() {
    const allValues = form.getFieldsValue();
    if (typeof onSubmit === 'function') {
      onSubmit(allValues);
    }
    navigate(-1);
  }

  const stepsItems = steps.map((step, index) => ({
    title: step.title,
    description: step.description,
    status: index < current ? 'finish' : index === current ? 'process' : 'wait',
  }));

  const modalTitle = moduleName ? `${title} — ${moduleName}` : title;

  const footer = (
    <div className="wizard-footer">
      <Button onClick={handleClose}>Cancel</Button>
      <div className="wizard-footer-nav">
        {!isFirstStep && <Button onClick={handleBack}>Back</Button>}
        {isLastStep ? (
          <Button type="primary" onClick={handleSubmit}>
            Submit
          </Button>
        ) : (
          <Button type="primary" onClick={handleNext}>
            Next
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      open
      title={modalTitle}
      footer={footer}
      onCancel={handleClose}
      width="min(680px, calc(100vw - 32px))"
      centered
      className="wizard-modal"
    >
      <div className="wizard-steps-container">
        <Steps current={current} items={stepsItems} size="small" className="wizard-steps" />
      </div>

      <div className="wizard-body">
        <Form form={form} layout="vertical">
          {steps
            .filter((s) => !s.isReview)
            .map((step, index) => (
              <div
                key={step.key}
                style={{ display: index === current ? 'block' : 'none' }}
              >
                <Row gutter={16}>
                  {(step.fields || []).map((field) => (
                    <Col span={field.span || 24} key={field.name}>
                      <Form.Item
                        name={field.name}
                        label={field.label}
                        rules={[
                          ...(field.required ? [{ required: true, message: `${field.label} is required` }] : []),
                          ...(field.rules || []),
                        ]}
                      >
                        {renderFieldControl(field)}
                      </Form.Item>
                    </Col>
                  ))}
                </Row>
              </div>
            ))}

          {currentStep.isReview && <ReviewStep steps={steps} form={form} />}
        </Form>
      </div>
    </Modal>
  );
}
